import React, { useCallback, useEffect, useState } from "react";
import { apptApi, apiError } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Button, Badge, Modal, Field, Loading } from "../../Components/UI/ui";
import { GATE, TERMINAL, maskPhone, fmtDateTime, notificationLine } from "../../lib/lab";

// Patient WhatsApp notifications for a booking (appointments doc §3.5b).
// The confirmation is sent automatically on booking / referral acceptance;
// here the desk resends it or sends a reminder before the appointment.
const NotifyPatient = ({ appointment, refreshKey }) => {
  const { can } = useAuth();
  const { toast } = useApp();
  const [log, setLog] = useState(null);      // { notifications, lastNotification }
  const [busy, setBusy] = useState(null);
  const [history, setHistory] = useState(false);
  const [toOther, setToOther] = useState(null);  // type being sent to another number
  const [phone, setPhone] = useState("");
  const [err, setErr] = useState(null);
  const id = appointment?._id;

  const load = useCallback(async () => {
    if (!id) return;
    try { setLog(await apptApi.notifications(id)); } catch { setLog(null); }
  }, [id]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const send = async (type, toPhone) => {
    setBusy(type); setErr(null);
    try {
      const res = await apptApi.notify(id, { type, ...(toPhone ? { phone: toPhone } : {}) });
      toast.success(res?.notification ? notificationLine(res.notification) : res?.message || `${type} sent`);
      setToOther(null); setPhone("");
      load();
    } catch (e) {
      const msg = apiError(e);
      if (toOther) setErr(msg); else toast.error(msg);
      load(); // a rejected send is still logged
    } finally { setBusy(null); }
  };

  if (!appointment) return null;
  const last = log?.lastNotification;
  const terminal = TERMINAL.has(appointment.status);              // CANCELLED / NO_SHOW / COMPLETED
  const past = new Date(appointment.scheduledAt).getTime() < Date.now();
  const mayNotify = can(...GATE.notifyPatient) && !terminal;
  const reminderWhy = terminal ? `This booking is ${appointment.status.toLowerCase().replace("_", " ")}` : past ? "The appointment time has passed" : "";

  return (
    <div className="lab-row" style={{ gap: 6, flexWrap: "wrap" }}>
      {mayNotify && (
        <>
          <Button size="sm" variant="secondary" loading={busy === "Reminder"} disabled={past} title={reminderWhy || "WhatsApp the patient a reminder"} onClick={() => send("Reminder")}>
            Send reminder
          </Button>
          <Button size="sm" variant={last?.status === "failed" ? "danger" : "ghost"} loading={busy === "Confirmation"} title="WhatsApp the booking confirmation again" onClick={() => send("Confirmation")}>
            {last?.status === "failed" ? "Retry confirmation" : "Resend confirmation"}
          </Button>
          <Button size="sm" variant="ghost" title="Send to a different number" onClick={() => { setToOther("Reminder"); setPhone(""); setErr(null); }}>To another number…</Button>
        </>
      )}
      {last && (
        <button type="button" className="lab-link" onClick={() => setHistory(true)} title="Notification history">
          {last.status === "sent"
            ? <Badge kind="on">{last.type} sent ✓ {fmtDateTime(last.sentAt).split(", ").pop()}{log.notifications.length > 1 ? ` · ${log.notifications.length}` : ""}</Badge>
            : <Badge kind="unpaid">{last.type} not delivered</Badge>}
        </button>
      )}
      {!last && log && !terminal && <span className="lab-muted" style={{ fontSize: 12 }}>No WhatsApp sent for this booking yet</span>}

      <Modal open={!!toOther} onClose={() => setToOther(null)} size="sm" title="Send to another number"
        footer={<>
          {err && <span className="lab-danger-text" style={{ marginRight: "auto" }}>{err}</span>}
          <Button onClick={() => setToOther(null)}>Cancel</Button>
          <Button variant="primary" loading={!!busy} disabled={phone.replace(/\D/g, "").length < 10} onClick={() => send(toOther, phone.replace(/\D/g, "").slice(-10))}>Send {toOther?.toLowerCase()}</Button>
        </>}>
        <div className="lab-muted">For an attendant or relative. The patient's own number is used by default.</div>
        <Field label="Message">
          <select className="lab-select w-full" value={toOther || "Reminder"} onChange={(e) => setToOther(e.target.value)}>
            <option value="Reminder">Reminder</option>
            <option value="Confirmation">Confirmation</option>
          </select>
        </Field>
        <Field label="WhatsApp number" hint="10 digits">
          <input className="lab-input w-full" value={phone} autoFocus inputMode="numeric" onChange={(e) => setPhone(e.target.value)} />
        </Field>
      </Modal>

      <Modal open={history} onClose={() => setHistory(false)} title="Patient notifications" footer={<Button onClick={() => setHistory(false)}>Close</Button>}>
        {!log ? <Loading /> : log.notifications?.length ? (
          <table className="lab-lines">
            <thead><tr><th>When</th><th>Message</th><th>To</th><th>Sent by</th><th>Status</th></tr></thead>
            <tbody>
              {[...log.notifications].reverse().map((n, i) => (
                <tr key={i}>
                  <td>{fmtDateTime(n.sentAt)}</td>
                  <td>{n.type}</td>
                  <td>{maskPhone(n.to)}</td>
                  <td className="lab-muted">{n.auto ? "System · automatic" : n.sentBy?.name || "—"}</td>
                  <td>{n.status === "sent" ? <Badge kind="on">Sent</Badge> : <><Badge kind="unpaid">Failed</Badge>{n.error && <div className="lab-danger-text" style={{ fontSize: 11 }}>{n.error}</div>}</>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <span className="lab-muted">Nothing sent yet.</span>}
      </Modal>
    </div>
  );
};

export default NotifyPatient;
